/**
 * Accounting — profit by year, month and day, everything paid and received,
 * expenses and salaries. STORY-172, decisions AC-D1–AC-D22.
 *
 * **The money rules live here, once**, so the server's totals and anything the
 * screen works out cannot disagree. Every function is pure: no clock, no
 * database, no clinic data.
 *
 * Money is integer minor units of the clinic's currency throughout, never
 * floats. A date is a calendar day as `YYYY-MM-DD` text, compared as text.
 *
 * The rules, in one place (WORKING-PLAN §6.9):
 * - money in is payments, on the day received (AC-D1);
 * - materials are supplier orders, on the day received, at the invoice price (AC-D2);
 * - a day is money in minus materials — no salary, no expense (AC-D5, AC-D8);
 * - a month or a year also takes off salaries and expenses;
 * - a salary is monthly, dated, kept as history (AC-D3, AC-D10), counted for the
 *   days employed (AC-D11), split by days when it changes mid-month (AC-D19),
 *   and counted up to today in the current month (AC-D17);
 * - a salary splits across locations by rota minutes (AC-D7), or evenly across
 *   the person's locations when they have none (AC-D12); a shared expense by the
 *   team's rota minutes (AC-D9); shares are rounded so they add back to the
 *   exact amount (AC-D22).
 *
 * And beside that cash figure, the work booked (WORKING-PLAN §6.9, ER-D1–ER-D8):
 * - Expected is every booking with a patient but a cancelled one or a no-show,
 *   Realized the completed ones (ER-D2), each worth its price minus its tray
 *   (ER-D3, ER-D5), rounded once per appointment (ER-D6) — see `appointmentWork`.
 */

import type { MoneyInCurrency } from "./currency";
import type { AppointmentStatusName, PaymentMethodName, RoleName } from "./index";
import { roundMinor, trayCost, type CostedLine } from "./materials-cost";

/* ── Dates ───────────────────────────────────────────────────────────────── */

/** `YYYY-MM-DD` for a year, month (1–12) and day. */
export function isoDay(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Days in a month, `month` 1–12. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/* ── Salaries ────────────────────────────────────────────────────────────── */

/** One dated salary. A new one takes effect from its date (AC-D10). */
export interface DatedSalary {
  /** `YYYY-MM-DD`. */
  effectiveFrom: string;
  monthlyCents: number;
}

/** What a month's salary cost needs to know about one person. */
export interface SalaryPersonInput {
  /** First day employed, or null when nobody recorded it. */
  startedOn: string | null;
  /** Last day employed (AC-D18), or null while they are still here. */
  endedOn: string | null;
  /** Any order. */
  salaries: readonly DatedSalary[];
}

/** The salary in force on a day: the newest one dated on or before it. */
export function salaryOn(salaries: readonly DatedSalary[], day: string): DatedSalary | null {
  let found: DatedSalary | null = null;
  for (const s of salaries) {
    if (s.effectiveFrom <= day && (found === null || s.effectiveFrom > found.effectiveFrom))
      found = s;
  }
  return found;
}

/**
 * One person's salary cost for one month, and how many days it counted.
 *
 * Each day counts `salary in force that day ÷ days in the month`, for every day
 * they were employed up to `countUntil`. So joining on the 16th of a 30-day month
 * counts 15 days' worth (AC-D11), a raise on the 16th costs 15 days at each
 * figure (AC-D19), and on the 5th of the current month five days have counted
 * (AC-D17). **A person with no start date counts from their first salary** —
 * a day with no salary in force simply costs nothing (AC-D18).
 *
 * Rounded to the minor unit once, for the whole month: rounding each day would
 * lose up to half a cent thirty times over.
 *
 * `countUntil` is the last day to count — the month's last day, or today in the
 * current month. A month that ends before it is counted in full.
 */
export function salaryForMonth(
  person: SalaryPersonInput,
  year: number,
  month: number,
  countUntil: string,
): { cents: number; days: number } {
  const n = daysInMonth(year, month);
  let sum = 0;
  let days = 0;
  for (let d = 1; d <= n; d++) {
    const day = isoDay(year, month, d);
    if (day > countUntil) break;
    if (person.startedOn !== null && day < person.startedOn) continue;
    if (person.endedOn !== null && day > person.endedOn) continue;
    const salary = salaryOn(person.salaries, day);
    if (salary === null) continue;
    sum += salary.monthlyCents / n;
    days++;
  }
  return { cents: Math.round(sum), days };
}

/* ── Splitting across locations ──────────────────────────────────────────── */

/**
 * Split a whole number of minor units by weights, so the parts **always add back
 * to the total** (AC-D22): every part is rounded down, and the units left over go
 * one each to the largest remainders. Ties go to the key that sorts first, so
 * the same inputs always give the same split.
 *
 * Weights need not add up to anything; zero and negative weights get nothing.
 * When no weight is positive the result is empty and the caller decides — that
 * is never a silent loss, because every caller checks for it first.
 */
export function allocate(
  total: number,
  weights: Readonly<Record<string, number>>,
): Record<string, number> {
  const keys = Object.keys(weights)
    .filter((k) => (weights[k] ?? 0) > 0)
    .sort();
  const sum = keys.reduce((a, k) => a + (weights[k] ?? 0), 0);
  if (keys.length === 0 || sum <= 0) return {};
  const raw = keys.map((k) => (total * (weights[k] ?? 0)) / sum);
  const parts = raw.map((r) => Math.floor(r));
  let left = total - parts.reduce((a, b) => a + b, 0);
  const order = keys
    .map((_, i) => i)
    .sort((a, b) => (raw[b] ?? 0) - (parts[b] ?? 0) - ((raw[a] ?? 0) - (parts[a] ?? 0)) || a - b);
  for (let i = 0; left > 0 && order.length > 0; i = (i + 1) % order.length, left--) {
    const at = order[i] as number;
    parts[at] = (parts[at] ?? 0) + 1;
  }
  return Object.fromEntries(keys.map((k, i) => [k, parts[i] ?? 0]));
}

/**
 * How one person's month splits across locations: by the rota minutes they
 * worked at each (AC-D7), or — with no rota minutes at all — evenly across the
 * locations they belong to (AC-D12). Returns weights for `allocate`.
 */
export function personWeights(
  rotaMinutes: Readonly<Record<string, number>>,
  memberOf: readonly string[],
): Record<string, number> {
  const worked = Object.entries(rotaMinutes).filter(([, minutes]) => minutes > 0);
  if (worked.length > 0) return Object.fromEntries(worked);
  return Object.fromEntries(memberOf.map((branchId) => [branchId, 1]));
}

/* ── Expected and realized revenue (STORY-180a, ER-D2–ER-D7) ─────────────── */

/**
 * The statuses Expected counts: every booking except a cancelled one and a
 * no-show, finished ones included (ER-D2). Realized is `COMPLETED` alone.
 */
export const EXPECTED_STATUSES = [
  "NOT_CHECKED_IN",
  "CONFIRMED",
  "CHECKED_IN",
  "IN_CHAIR",
  "COMPLETED",
] as const satisfies readonly AppointmentStatusName[];

/** One line of a procedure's tray, as an appointment's materials need it. */
export interface WorkTrayLine extends CostedLine {
  /** A per-tooth line counts once per tooth, a per-visit one once (TP15, TP16). */
  perTooth: boolean;
}

/** What one appointment's figure needs to know about it (ER-D3). */
export interface AppointmentWorkInput {
  /**
   * The prices of the course steps this booking fulfils, copied when it was
   * booked — one per tooth (BF6). Empty when it fulfils none.
   */
  stepPrices: readonly number[];
  /** The distinct teeth of those steps. Empty for a whole-mouth course. */
  stepTeeth: readonly number[];
  /** The teeth the booking itself names (BF3). */
  bookedTeeth: readonly number[];
  /** Its procedure, or null for a booking that names none. */
  procedure: {
    priceCents: number;
    perTooth: boolean;
    tray: readonly WorkTrayLine[];
  } | null;
}

/** One appointment's price, materials and profit, in minor units. */
export interface AppointmentWork {
  priceCents: number;
  materialsCents: number;
  profitCents: number;
  /** False for a booking with no procedure and no course step: it counts zero (ER-D3). */
  priced: boolean;
}

/**
 * What one appointment is worth — **the one copy of ER-D3**.
 *
 * - **Teeth**: the distinct teeth of the course steps it fulfils, else the
 *   booking's own teeth, and never fewer than one (TP19).
 * - **Price**: the sum of those steps' booked prices when there are any —
 *   already one per tooth — else the procedure's catalogue price, times the
 *   teeth for a per-tooth procedure.
 * - **Materials**: the procedure's tray, a per-tooth line times the teeth and a
 *   per-visit line once — on **every** visit of a course, as stock deducts it
 *   (ER-D5). Exact, then rounded once (MC-D5).
 * - **Profit**: price minus that rounded figure, so the three always reconcile.
 *
 * A course visit booked at a location whose catalogue lacks the procedure
 * names none (`PlansService.bookPart`): it keeps its step prices and costs no
 * materials, because it has no tray to read.
 */
export function appointmentWork(input: AppointmentWorkInput): AppointmentWork {
  const teethFrom = input.stepTeeth.length > 0 ? input.stepTeeth : input.bookedTeeth;
  const teeth = Math.max(1, new Set(teethFrom).size);

  const fromSteps = input.stepPrices.length > 0;
  const { procedure } = input;
  if (!fromSteps && procedure === null) {
    return { priceCents: 0, materialsCents: 0, profitCents: 0, priced: false };
  }

  const priceCents = fromSteps
    ? input.stepPrices.reduce((sum, cents) => sum + cents, 0)
    : (procedure?.priceCents ?? 0) * (procedure?.perTooth ? teeth : 1);

  const materialsCents =
    procedure === null
      ? 0
      : roundMinor(
          trayCost(
            procedure.tray.map((line) => ({
              ...line,
              quantity: line.quantity * (line.perTooth ? teeth : 1),
            })),
          ),
        );

  return { priceCents, materialsCents, profitCents: priceCents - materialsCents, priced: true };
}

/* ── Billed, owed and overhead (STORY-182a, AF-D1–AF-D4) ──────────────────── */

/** The age buckets of what is still owed, by days since the invoice was issued (AF-D3). */
export const OWED_BUCKETS = [
  { key: "0-30", from: 0, to: 30 },
  { key: "31-60", from: 31, to: 60 },
  { key: "61-90", from: 61, to: 90 },
  { key: "91+", from: 91, to: Infinity },
] as const;

export type OwedBucketKey = (typeof OWED_BUCKETS)[number]["key"];

/** The bucket an invoice of this many days old falls in. Negative ages (a clock skew) count as new. */
export function owedBucket(daysOld: number): OwedBucketKey {
  const days = Math.max(0, Math.floor(daysOld));
  return (
    OWED_BUCKETS.find((bucket) => days >= bucket.from && days <= bucket.to) ?? OWED_BUCKETS[0]
  ).key;
}

/** Whole calendar days from one `YYYY-MM-DD` to a later one. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/**
 * What is still owed on one invoice: its total less what was paid, never
 * negative — an overpaid invoice owes nothing, it does not offset another (AF-D3).
 */
export function owedOn(totalCents: number, paidCents: number): number {
  return Math.max(0, totalCents - paidCents);
}

/**
 * Collected over billed, as a fraction (AF-D2). **Null with nothing billed**, never
 * zero — a rate of 0% would read as "nobody paid". It can exceed 1, because a
 * payment this period may settle a bill issued in an earlier one.
 */
export function collectionRate(collectedCents: number, billedCents: number): number | null {
  return billedCents > 0 ? collectedCents / billedCents : null;
}

/**
 * Money out over money in, as a fraction (AF-D4). Null when nothing came in, for
 * the same reason as `collectionRate`.
 */
export function overheadRate(moneyOutCents: number, moneyInCents: number): number | null {
  return moneyInCents > 0 ? moneyOutCents / moneyInCents : null;
}

/**
 * The share of what is owed that is more than 60 days old — the 61–90 and 91+
 * buckets over the whole (AF-D6, the research's "no more than 15%"). Null when
 * nothing is owed, so an empty book is not "0% overdue".
 */
export function owedOver60Share(
  buckets: readonly { key: OwedBucketKey; cents: number }[],
): number | null {
  const total = buckets.reduce((sum, bucket) => sum + bucket.cents, 0);
  return total <= 0 ? null : owedOver60Cents(buckets) / total;
}

/** What is owed that is more than 60 days old: the 61–90 and 91+ buckets. */
export function owedOver60Cents(buckets: readonly { key: OwedBucketKey; cents: number }[]): number {
  return buckets
    .filter((bucket) => bucket.key === "61-90" || bucket.key === "91+")
    .reduce((sum, bucket) => sum + bucket.cents, 0);
}

/* ── Groups and targets (STORY-182c, AF-D5, AF-D6) ─────────────────────────── */

/**
 * The groups an expense category can be placed in (AF-D5). Staff and materials
 * are not categories — salaries and supplier orders are always staff and
 * supplies — so they are not choices here. Every category starts as `OTHER`.
 */
export const EXPENSE_GROUPS = ["SUPPLIES_LAB", "OCCUPANCY", "OTHER"] as const;
export type ExpenseGroup = (typeof EXPENSE_GROUPS)[number];

/** Which `OverheadGroups` entry an expense category's group adds to. */
export function overheadKeyOf(group: ExpenseGroup): "suppliesLab" | "occupancy" | "other" {
  switch (group) {
    case "SUPPLIES_LAB":
      return "suppliesLab";
    case "OCCUPANCY":
      return "occupancy";
    case "OTHER":
      return "other";
  }
}

/** The measures a clinic can set a target for, each with the direction it is judged in. */
export const ACCOUNTING_TARGET_MEASURES = [
  { key: "collectionRate", measure: "COLLECTION_RATE", direction: "atLeast" },
  { key: "overhead", measure: "OVERHEAD", direction: "atMost" },
  { key: "owedOver60", measure: "OWED_OVER_60", direction: "atMost" },
] as const;

export type AccountingTargetKey = (typeof ACCOUNTING_TARGET_MEASURES)[number]["key"];

/**
 * A clinic's targets, in **basis points** (9800 is 98.00%), `null` where none is
 * set. Never defaulted: a figure is coloured against a target only when
 * somebody set one (AF-D6).
 */
export type AccountingTargets = Record<AccountingTargetKey, number | null>;

/** The most a target may be, in basis points: 100.00%. The least is 1 (0.01%). */
export const TARGET_MAX_BASIS_POINTS = 10_000;

/**
 * Whether a figure meets its target — **in whole numbers**, `numerator ÷
 * denominator` against `basisPoints ÷ 10,000`, so 98.0% of 10,000 is met at
 * exactly 9,800 and never missed by a float's last digit. Null when there is
 * nothing to judge: no target, or nothing to divide by.
 */
export function meetsTarget(
  key: AccountingTargetKey,
  numerator: number,
  denominator: number,
  basisPoints: number | null,
): boolean | null {
  if (basisPoints === null || denominator <= 0) return null;
  const direction = ACCOUNTING_TARGET_MEASURES.find((m) => m.key === key)?.direction;
  const figure = numerator * TARGET_MAX_BASIS_POINTS;
  const goal = denominator * basisPoints;
  return direction === "atLeast" ? figure >= goal : figure <= goal;
}

/** Money out in the four groups AF-D4 names. They add up to `materials + salaries + expenses`. */
export interface OverheadGroups {
  /** Salaries. */
  staff: number;
  /** Materials received, and expense categories placed in this group. */
  suppliesLab: number;
  /** Expense categories placed in this group. */
  occupancy: number;
  /** Expense categories placed here — which is where every category starts (AF-D5). */
  other: number;
}

/** One age bucket of what is owed. */
export interface OwedBucket {
  key: OwedBucketKey;
  cents: number;
  /** Invoices with something still owing in it. */
  count: number;
}

/** What is owed **as of today**, whatever year is on screen (AF-D3). */
export interface AccountingOwed {
  /** The clinic's today, `YYYY-MM-DD`. */
  asOf: string;
  totalCents: number;
  invoiceCount: number;
  /** Always the four buckets, in order. */
  buckets: OwedBucket[];
}

/* ── Today (STORY-183a, DY-D1–DY-D6) ─────────────────────────────────────── */

/**
 * What is still to come today: expected minus collected, **never below zero**
 * (DY-D3). A day that has collected more than it expected — payments on older
 * invoices count as collected (DY-D2) — reads zero, not a negative.
 */
export function stillToCome(expectedCents: number, collectedCents: number): number {
  return Math.max(0, expectedCents - collectedCents);
}

/** One hour of today: what its bookings are worth, and how much of that is finished. */
export interface AccountingTodayHour {
  /** 0–23, on the booking's own location's clock (DY-D5). */
  hour: number;
  expectedCents: number;
  finishedCents: number;
}

/** One of today's bookings — **never the patient** (DY-D6). */
export interface AccountingTodayBooking {
  /** `HH:MM` on its own location's clock. */
  time: string;
  hour: number;
  /** The catalogue procedure's name, or null for a booking that names none. */
  procedure: string | null;
  status: AppointmentStatusName;
  /** Full price, as ER-D3 prices a booking. */
  priceCents: number;
}

/** What is expected to come today, for the chosen locations (DY-D1–DY-D6). */
export interface AccountingToday {
  /** ISO 4217, the selection's. */
  currency: string;
  /** The clinic's today, `YYYY-MM-DD`. */
  date: string;
  /** Every booking with a patient but cancelled and no-show — finished ones included. */
  expected: WorkTotals;
  /** The completed ones. */
  finished: WorkTotals;
  /** Every payment received today, on older invoices too (DY-D2). */
  collectedCents: number;
  /** Expected minus collected, never below zero (DY-D3). */
  stillToComeCents: number;
  /** Counted bookings with no procedure, so worth zero (ER-D3). */
  unpriced: number;
  /** Every hour from the first booking to the last, empty ones as zero. */
  hours: AccountingTodayHour[];
  /** In time order. */
  bookings: AccountingTodayBooking[];
}

/* ── Team: providers and chairs (STORY-184a, TM-D1–TM-D5) ─────────────────── */

/**
 * What an hour of chair time earned: `cents` over `minutes`, rounded half up to
 * the minor unit (TM-D3). **Null at zero minutes** — no hours is not "earned
 * nothing an hour".
 */
export function perHourCents(cents: number, minutes: number): number | null {
  if (minutes <= 0) return null;
  const exact = (cents * 60) / minutes;
  return cents < 0 ? -Math.round(-exact) : Math.round(exact);
}

/** The bookings of one provider or one chair in a period, expected and realized (TM-D1). */
export interface TeamFigures {
  /** Every counted booking (ER-D2). */
  expected: WorkTotals;
  /** The completed ones. */
  finished: WorkTotals;
  /** Booked minutes: each counted booking's own length, start to end (TM-D3). */
  bookedMinutes: number;
  /** The completed bookings' minutes. */
  finishedMinutes: number;
}

/** One provider — or, with a null `userId`, the bookings that name none (TM-D2). */
export interface TeamProvider extends TeamFigures {
  userId: string | null;
  /** "No provider" for the null row. */
  name: string;
  job: RoleName | null;
}

/** One chair, listed while it has bookings in the period, retired or not (TM-D4). */
export interface TeamChair extends TeamFigures {
  chairId: string;
  name: string;
  branchId: string;
  branchName: string;
}

/**
 * Providers and chairs for a year, or one month of it (`month` 0 is the whole
 * year), for the chosen locations. Each list adds up to the same whole; neither
 * names a patient (DY-D6).
 */
export interface AccountingTeam {
  /** ISO 4217, the selection's. */
  currency: string;
  year: number;
  /** 0 for the whole year, else 1–12. */
  month: number;
  providers: TeamProvider[];
  chairs: TeamChair[];
}

/** A count of appointments and what they add up to. Every amount in minor units. */
export interface WorkTotals {
  count: number;
  priceCents: number;
  materialsCents: number;
  profitCents: number;
}

/* ── What the API returns ───────────────────────────────────────────────── */

/** A location the caller may read accounts for. */
export interface AccountingLocation {
  id: string;
  name: string;
  /**
   * ISO 4217, the location's own (QA4-D6). **A selection is of locations
   * sharing one currency** — the API refuses a mix, because their figures
   * cannot be added.
   */
  currency: string;
}

/**
 * What a selection in a currency other than the home currency does **not** take
 * off its profit (QA4-D14): its share of salaries and shared expenses, and what
 * it bought from suppliers — all in the home currency, so shown beside the
 * profit and never subtracted from it. Minor units of `AccountingYear.homeCurrency`.
 */
export interface NotTakenOff {
  salaries: number;
  materials: number;
  expenses: number;
}

/** One month for the selected locations. Every amount in minor units. */
export interface AccountingMonth {
  /** 1–12. */
  month: number;
  /** False for a month that has not begun: every amount is then zero. */
  started: boolean;
  /** The month today is in — counted up to today (AC-D17). */
  current: boolean;
  moneyIn: number;
  materials: number;
  salaries: number;
  expenses: number;
  /** `moneyIn − materials − salaries − expenses`. */
  profit: number;
  /** Money in by how it was paid. */
  byMethod: Partial<Record<PaymentMethodName, number>>;
  /** Money in by location id. */
  byLocation: Record<string, number>;
  bySupplier: { supplierId: string; name: string; cents: number }[];
  /** This selection's share of each person's salary. */
  byPerson: { userId: string; name: string; job: RoleName; cents: number }[];
  byCategory: { categoryId: string; cents: number }[];
  /**
   * Every counted booking this month — Expected (ER-D2). Filled for a month
   * that has not begun too: bookings ahead are the point of it.
   */
  booked: WorkTotals;
  /** The completed ones — Realized. */
  finished: WorkTotals;
  /** Counted bookings with no procedure, so worth zero (ER-D3). */
  unpriced: number;
  /** Invoices issued this month, live ones only (AF-D1), and how many. */
  billed: number;
  billedCount: number;
  /** Money out in four groups (AF-D4); they add up to materials + salaries + expenses. */
  overhead: OverheadGroups;
  /**
   * Null when the selection is in the home currency, as every single-currency
   * clinic's is: then nothing is set aside and the figures are what they were.
   */
  notTakenOff: NotTakenOff | null;
  /** Booked and finished profit by procedure category; null for no procedure. */
  byProcedureCategory: { categoryId: string | null; booked: number; finished: number }[];
}

/** One day's booked and finished profit (ER-D2), for a day that has any. */
export interface AccountingWorkDay {
  date: string;
  booked: number;
  finished: number;
  bookedCount: number;
  finishedCount: number;
}

/** A procedure category a month's figures name — the clinic's own row (PG-D1). */
export interface AccountingProcedureCategory {
  id: string;
  name: string;
  /** A palette key (`PROCEDURE_CATEGORY_COLOURS`), never a colour value. */
  colour: string;
}

/** One day: money in minus materials, nothing else (AC-D5, AC-D8). */
export interface AccountingDay {
  date: string;
  /** Invoices issued that day (AF-D1) — alongside money in, never part of profit. */
  billed: number;
  moneyIn: number;
  materials: number;
  profit: number;
}

export interface AccountingYear {
  year: number;
  /** ISO 4217 — every figure here is in it, but `notTakenOff` (QA4-D6). */
  currency: string;
  /** The clinic's home currency, which `notTakenOff` is in. */
  homeCurrency: string;
  /**
   * Money received at these locations this year **in another currency** — on
   * invoices issued before a location changed its currency. Kept out of every
   * figure, never converted, listed so it is not lost.
   */
  notCounted: MoneyInCurrency[];
  /** Today in the clinic's own timezone, `YYYY-MM-DD`. */
  today: string;
  /** The location ids the figures cover. */
  locations: string[];
  /** Always twelve. */
  months: AccountingMonth[];
  /** Every day of the year up to today. */
  days: AccountingDay[];
  /** Every day of the year with a counted booking, oldest first — today's future too. */
  work: AccountingWorkDay[];
  /** The categories `byProcedureCategory` names, in the clinic's order. */
  procedureCategories: AccountingProcedureCategory[];
  /** What is owed now — the same for any year asked (AF-D3). */
  owed: AccountingOwed;
}

export interface AccountingYearTotal {
  year: number;
  moneyIn: number;
  moneyOut: number;
  profit: number;
  /** Invoices issued in the year (AF-D1). */
  billed: number;
  /** Booked profit — Expected (ER-D2). */
  expected: number;
  /** Finished profit — Realized. */
  realized: number;
  /** In the home currency, not taken off the profit (QA4-D14). Zero at home. */
  notTakenOff: number;
}

/** Every year that holds any money, oldest first, for the selected locations. */
export interface AccountingYears {
  currency: string;
  homeCurrency: string;
  years: AccountingYearTotal[];
}

/* ── Expenses ────────────────────────────────────────────────────────────── */

/** The clinic's own list (AC-D4) — never an enum. */
export interface ExpenseCategory {
  id: string;
  name: string;
  /** Where its expenses count in the overhead split (AF-D5). */
  group: ExpenseGroup;
  /** Expenses filed under it. A category in use cannot be removed. */
  expenseCount: number;
}

export interface Expense {
  id: string;
  /** `YYYY-MM-DD`, the day it was paid. */
  paidOn: string;
  categoryId: string;
  /** Null is *shared*, split by the team's rota (AC-D9). */
  branchId: string | null;
  amountCents: number;
  /** ISO 4217 as recorded: the location's, or the home currency if shared (QA4-D6). */
  currency: string;
  note: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * An expense as listed for a selection of locations: `countedCents` is what it
 * adds to that selection — the whole amount for a location in it, its share for
 * a shared one (AC-D9), never anything for a location outside it.
 */
export interface ExpenseRow extends Expense {
  countedCents: number;
}

export interface SaveExpense {
  paidOn: string;
  categoryId: string;
  branchId: string | null;
  amountCents: number;
  note?: string | null;
}

/** One change to an expense, from the audit log (AC-D21). */
export interface ExpenseChange {
  at: string;
  actorName: string | null;
  action: "CREATED" | "UPDATED" | "DELETED";
  before: (Omit<SaveExpense, "note"> & { note: string | null }) | null;
  after: (Omit<SaveExpense, "note"> & { note: string | null }) | null;
}

/* ── Salaries ────────────────────────────────────────────────────────────── */

export interface SalaryEntry {
  id: string;
  effectiveFrom: string;
  monthlyCents: number;
  createdByName: string | null;
  createdAt: string;
}

/** One person on the Salaries tab. Only ever sent to Admin and Finance officer (AC-D13). */
export interface SalaryPerson {
  userId: string;
  name: string;
  job: RoleName;
  isActive: boolean;
  startedOn: string | null;
  endedOn: string | null;
  /**
   * True when `endedOn` was not recorded but taken from their account's last
   * change, for somebody deactivated with no last day (QF-D7) — the screen says
   * so, so an administrator can set the true one.
   */
  endedOnAssumed: boolean;
  /** Oldest first. */
  salaries: SalaryEntry[];
  /** The current month so far, across every location they work at. */
  thisMonth: {
    days: number;
    daysInMonth: number;
    cents: number;
    /** False when they have no rota minutes and the split is even (AC-D12). */
    byRota: boolean;
    split: { branchId: string; minutes: number; cents: number }[];
  };
}

export interface SaveSalary {
  effectiveFrom: string;
  monthlyCents: number;
}

export interface SetLastDay {
  endedOn: string | null;
}
